import { TableSortLabel, Paper, Alert, IconButton } from "@mui/material";
import React, { useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Stack } from "@mui/system";
import {
  checkIfMetricsExist,
  getValueForDashBoard,
  roundStringNumberToInt,
} from "../../helpers/businessleaderDashboardHelpers";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import {
  additionalQueriesrows02,
  additionalQueriestableHeadings02,
} from "./AdditionalQueriesConstants";
import { saveAs } from "file-saver";
import DownloadIcon from "@mui/icons-material/Download";

interface Props {
  last7DaysSince2DaysAgoData: any;
}

export const TrendsOverPastSevenDays: React.FC<Props> = React.memo(
  ({ last7DaysSince2DaysAgoData }) => {
    const headCells = Object.entries(additionalQueriestableHeadings02);

    const [order, setOrder] = useState("desc") as any;
    const [orderBy, setOrderBy] = useState("Metrics") as any;

    const dayBeforeYesterday = dayjs().subtract(2, "days").format("DD-MM-YYYY"); // 2 days ago
    const sevenDaysAgoFromYesterday = dayjs()
      .subtract(9, "day")
      .startOf("day")
      .format("DD-MM-YYYY"); // 7 days ago
    const eightDaysAgoFromYesterday = dayjs()
      .subtract(10, "day")
      .startOf("day")
      .format("DD-MM-YYYY"); // 7 days ago
    const fortheenDaysAgoFromYesterday = dayjs()
      .subtract(16, "day")
      .startOf("day")
      .format("DD-MM-YYYY"); // 14 days ago

    const parseValue = (value: any) => {
      if (typeof value === "string") {
        if (value.includes("%")) {
          return parseFloat(value.replace("%", ""));
        }
        if (value.includes("$")) {
          return parseFloat(value.replace("$", ""));
        } else {
          return value.toLowerCase();
        }
      } else if (value === null || !value) {
        return "";
      }
      return value;
    };

    const handleRequestSort = (property: any) => {
      const isAscending = orderBy === property && order === "asc";
      setOrder(isAscending ? "desc" : "asc");
      setOrderBy(property);
    };

    const sortedRows = [...additionalQueriesrows02].sort((a, b) => {
      let valuea = "";
      let valueb = "";
      let dataa = getValueForDashBoard(
        a["metric"],
        last7DaysSince2DaysAgoData
      ) as any;
      if (dataa) {
        valuea = dataa[orderBy];
      }
      let datab = getValueForDashBoard(
        b["metric"],
        last7DaysSince2DaysAgoData
      ) as any;
      if (datab) {
        valueb = datab[orderBy];
      }
      const aValue = parseValue(valuea);
      const bValue = parseValue(valueb);

      if (aValue < bValue) {
        return order === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === "asc" ? 1 : -1;
      }
      return 0;
    });

    function getTrendValue(valueToReturn: string, colorValue: any) {
      if (colorValue === "success") {
        return (
          <>
            <TrendingUpIcon sx={{ mr: 1 }} color={colorValue} />
            <span>{valueToReturn + "%"}</span>
          </>
        );
      } else {
        return (
          <>
            <TrendingDownIcon color={colorValue} sx={{ mr: 1 }} />
            <span>{valueToReturn + "%"}</span>
          </>
        );
      }
    }

    const getMetricValue = (metricName: string, fieldName: string) => {
      const data = getValueForDashBoard(
        metricName,
        last7DaysSince2DaysAgoData
      ) as any;
      let valueToReturn = data[fieldName];
      if (
        valueToReturn !== null &&
        valueToReturn !== 0 &&
        fieldName !== "Metrics"
      ) {
        if (fieldName === "Trend") {
          let colorValue;
          if (valueToReturn > 0) {
            colorValue = "success";
          } else {
            colorValue = "error";
          }
          return getTrendValue(
            roundStringNumberToInt(valueToReturn),
            colorValue
          );
        } else if (metricName === "CVR") {
          return roundStringNumberToInt(valueToReturn * 100, true) + "%";
        } else if (metricName !== "FTD") {
          return roundStringNumberToInt(valueToReturn) + "$";
        } else {
          return roundStringNumberToInt(valueToReturn);
        }
      } else if (valueToReturn === null) {
        return "null";
      } else {
        return valueToReturn;
      }
    };

    const getMetricValueForCSV = (metricName: string, fieldName: string) => {
      const data = getValueForDashBoard(
        metricName,
        last7DaysSince2DaysAgoData
      ) as any;
      let valueToReturn = null as any;
      if (data) {
        valueToReturn = data[fieldName];
      }
      if (
        valueToReturn !== null &&
        valueToReturn !== 0 &&
        fieldName !== "Metrics"
      ) {
        if (fieldName === "Trend") {
          valueToReturn = Math.round(valueToReturn.toFixed(2));
          return valueToReturn + "%";
        } else if (metricName === "CVR") {
          valueToReturn = valueToReturn * 100;
          valueToReturn = Math.round(valueToReturn.toFixed(2));
          return valueToReturn + "%";
        } else if (metricName !== "FTD") {
          valueToReturn = Math.round(valueToReturn);
          return valueToReturn + "$";
        } else {
          valueToReturn = Math.round(valueToReturn);
          return valueToReturn;
        }
      } else if (valueToReturn === null) {
        return "null";
      } else {
        return valueToReturn;
      }
    };

    const handleExportCSV = () => {
      const csvRows = [];

      // Extract the table headers
      const headers = headCells.map((pair) => pair[0]) as any;
      csvRows.push(headers.join(","));

      // Extract the table data
      // eslint-disable-next-line
      sortedRows.map((row: any) => {
        let rowData = [] as any;
        // eslint-disable-next-line
        headers.map((key: any) => {
          const result = getMetricValueForCSV(row?.metric, key);
          if (result !== "null") {
            rowData.push(result);
          }
        });
        if (rowData?.length > 0) {
          csvRows.push(rowData.join(","));
        }
      });

      // Convert CSV rows to a single string
      const csvString = csvRows.join("\n");

      // Create a blob and download the file
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "TrendsOverPastSevenDays.csv");
    };

    return (
      <>
        <TableContainer
          className="RunRateTableWrap"
          component={Paper}
          sx={{ boxShadow: "none", maxHeight: 400, overflowY: "auto" }}
        >
          {sortedRows?.length > 0 ? (
            <>
              <Stack
                flexDirection={"row"}
                alignItems="center"
                gap={2}
                margin="4px 16px"
              >
                <Alert
                  sx={{
                    color: "#ED6C02",
                    margin: "16px",
                    border: "1px solid #ED6C0280",
                    width: "100%",
                  }}
                  severity="warning"
                  icon={<InfoOutlinedIcon fontSize="inherit" sx={{ mr: 1 }} />}
                >
                  PLEASE NOTE: Data is only relevant for the period of last 7
                  days: {sevenDaysAgoFromYesterday} - {dayBeforeYesterday} , and
                  last 8-14 ago:{"  "}
                  {fortheenDaysAgoFromYesterday} - {eightDaysAgoFromYesterday}
                </Alert>
                <IconButton color="secondary" size="large" data-tracker="276">
                  <DownloadIcon fontSize="medium" onClick={handleExportCSV} />
                </IconButton>
              </Stack>
              <Table aria-label="collapsible table" size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {headCells.map(([key, value], index) => (
                      <TableCell
                        key={index + "headCellSeven" + Math.random()}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === key}
                          direction={orderBy === key ? order : "asc"}
                          onClick={() => handleRequestSort(key)}
                        >
                          {value}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows?.map((row, rowIndex) => (
                    <React.Fragment
                      key={rowIndex + "sortedRowsTrend" + Math.random()}
                    >
                      {checkIfMetricsExist(
                        row?.metric,
                        last7DaysSince2DaysAgoData
                      ) ? (
                        <TableRow
                          sx={{
                            "&:not(:last-child) > *": {
                              borderBottom:
                                "1px solid rgba(224, 224, 224, 1) !important",
                            },
                          }}
                        >
                          <TableCell
                            key={1 + "tabelCellSeven" + Math.random()}
                            align="left"
                            sx={{ py: "3px", color: "#9c27b0" }}
                          >
                            <>
                              {getMetricValue(row?.metric, "Metrics")
                                ?.toString()
                                ?.replace(/_/g, " ")
                                ?.toUpperCase()}
                            </>
                          </TableCell>
                          <TableCell
                            key={2 + "tabelCellSeven" + Math.random()}
                            align="left"
                          >
                            {getMetricValue(row?.metric, "Last_week")}
                          </TableCell>
                          <TableCell
                            key={3 + "tabelCellSeven" + Math.random()}
                            align="left"
                          >
                            {getMetricValue(row?.metric, "Last_two_week")}
                          </TableCell>
                          <TableCell
                            key={4 + "tabelCellSeven" + Math.random()}
                            align="left"
                          >
                            <Stack sx={{ flexDirection: "row" }}>
                              {getMetricValue(row?.metric, "Trend")}
                              <> </>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ) : (
                        ""
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </>
          ) : (
            ""
          )}
        </TableContainer>
      </>
    );
  }
);
